import { Component, computed, inject, input, afterNextRender, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Faqs } from '../signup.types';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-faqs',
  imports: [
    NgbAccordionModule
  ],
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss'
})
export class FaqsComponent {

  //#region Services

  private sanitizer = inject(DomSanitizer);

  //#endregion

  //#region I/O

  step = input.required<number>();

  //#endregion

  //#region Properties

  faqs = signal<Faqs[]>([]);

  faqsInView = computed(() => this.faqs().find(x => x.step === this.step())?.data ?? []);

  //#endregion

  //#region Constructor

  constructor() {
    afterNextRender(() => {
      this.faqs.set([
        {
          step: 0,
          data: [
            {
              question: `What delivery options do you offer?`,
              answer: this.#sanitizeHtml(`
                <p class="fw-bold">
                  Home Delivery
                </p>
                <p>
                  We offer home deliveries every other week. Deliveries can be made to homes, offices or apartments. We proudly serve the metro Atlanta area and its wonderful surrounding communities. <br>
                  Deliveries are made Tuesday through Saturday between 9 and 6 pm. <br>
                  Your delivery day is assigned to you based on your location.
                </p>
                <p class="fw-bold">
                  Popup Pickup
                </p>
                <p>
                  Our Pop-Up Pick-Ups are held at a specific location on a certain day and time.<br>
                  For more information about Popup pickups, visit this <a href="./pages/atlanta-delivery-schedule">page</a>.
                </p>
                <p class="fw-bold">
                  Drop-off
                </p>
                <p>
                  We’ll deliver your pre-ordered items for you to pick up during the designated times.<br>
                  You’ll have plenty of time to collect your goodies.
                </p>
                <p>
                  <a href="./pages/atlanta-delivery-schedule">Click here for our delivery schedule</a>
                </p>
               `)
            },
            {
              question: `What if I’m not home for my Home Delivery?`,
              answer: this.#sanitizeHtml(`
               <p>
                 Our driver will place your order by your front door, orders will be left in the cooler with ice that you provide. 
               </p>
               `)
            }
          ]
        },
        {
          step: 1,
          data: [
            {
              question: `Is this a subscription service?`,
              answer: this.#sanitizeHtml(`
                <p>
                  While you can subscribe to your favorite items, this is not a requirement! Signing up is completely free and there are no obligations to purchase.
                </p>
               `)
            },
          ]
        },
        {
          step: 2,
          data: [
            {
              question: `Will you charge my card now?`,
              answer: this.#sanitizeHtml(`
               <p>
                No! We will only charge your credit card when you make your first purchase with us.
               </p>
              `)
            },
            {
              question: `How do I know my debit/credit card information is secure on your website?`,
              answer: this.#sanitizeHtml(`
               <p>
                 Your privacy and payment security is incredibly important to us. For this reason, we have invested in a secure credit card processing system through Authorize. As a PCI compliant service provider, Authorize ensures our customer’s credit card information is safe, secure and properly handled during the billing process.
               </p>
              `)
            }
          ]
        },
        {
          step: 3,
          data: [
            {
              question: `Do I need to have a subscription?`,
              answer: this.#sanitizeHtml(`
                <p>
                  Feel free to order whenever you need something! Plus, if you find yourself needing certain items more often, you can easily set up a subscription to make things even more convenient.
                </p>
              `)
            },
            {
              question: `What frequency options are available?`,
              answer: this.#sanitizeHtml(`You can choose from weekly, every other week or monthly.`)
            }
          ]
        }
      ]);
    })
  }

  //#endregion

  //#region Methods

  #sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  //#endregion
}
